<template>
  <transition name="cookie-fade">
    <div
      v-if="showModal"
      class="cookie-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
    >
      <div class="cookie-card">
        <img
          class="cookie-illustration"
          :src="mascot"
          alt=""
          aria-hidden="true"
        />
        <div class="cookie-content">
          <template v-if="!showSettings">
            <h2 id="cookie-modal-title" class="cookie-title">
              Wij gebruiken cookies
            </h2>
            <p class="cookie-text">
              Cookies helpen ons om Inviplay goed te laten werken en jouw
              ervaring te verbeteren. Je kunt zelf kiezen welke cookies je
              accepteert; zonder akkoord meten we alleen anoniem en zonder
              cookies. Lees meer in onze
              <a href="/privacy-policy">privacyverklaring</a>
              .
            </p>
            <div class="cookie-buttons">
              <button class="cookie-btn cookie-btn-primary" @click="accept">
                Accepteren
              </button>
              <button
                class="cookie-btn cookie-btn-outline"
                @click="showSettings = true"
              >
                Instellingen
              </button>
              <button class="cookie-link-btn" @click="reject">Weigeren</button>
            </div>
          </template>

          <template v-else>
            <h2 id="cookie-modal-title" class="cookie-title">
              Cookie-instellingen
            </h2>
            <div class="cookie-setting">
              <div class="cookie-setting-text">
                <span class="cookie-setting-name">Noodzakelijk</span>
                <span class="cookie-setting-desc">
                  Nodig om Inviplay goed te laten werken, zoals ingelogd
                  blijven.
                </span>
              </div>
              <span class="cookie-setting-fixed">Altijd aan</span>
            </div>
            <div class="cookie-setting">
              <div class="cookie-setting-text">
                <span class="cookie-setting-name">
                  Statistieken &amp; advertenties
                </span>
                <span class="cookie-setting-desc">
                  Gebruiksstatistieken gekoppeld aan je account (PostHog, Google
                  Analytics) en het meten van advertenties (Meta).
                </span>
              </div>
              <button
                class="cookie-toggle"
                :class="{ 'cookie-toggle-on': trackingEnabled }"
                role="switch"
                :aria-checked="trackingEnabled ? 'true' : 'false'"
                aria-label="Statistieken en advertenties"
                @click="trackingEnabled = !trackingEnabled"
              >
                <span class="cookie-toggle-knob" />
              </button>
            </div>
            <div class="cookie-buttons">
              <button class="cookie-btn cookie-btn-primary" @click="save">
                Keuze opslaan
              </button>
              <button class="cookie-link-btn" @click="showSettings = false">
                Terug
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>
import mascot from '@/assets/images/cookie-mascot.png'
import {
  getConsent,
  setConsent,
  CONSENT_ACCEPTED,
  CONSENT_REJECTED
} from '@/utils/consent'

export default {
  name: 'CookieModal',
  data() {
    return {
      showModal: getConsent() === null,
      showSettings: false,
      trackingEnabled: false,
      mascot
    }
  },
  methods: {
    accept() {
      this.choose(CONSENT_ACCEPTED, 'dialog')
    },
    reject() {
      this.choose(CONSENT_REJECTED, 'dialog')
    },
    save() {
      this.choose(
        this.trackingEnabled ? CONSENT_ACCEPTED : CONSENT_REJECTED,
        'settings'
      )
    },
    choose(value, source) {
      setConsent(value, source)
      this.showModal = false
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.cookie-overlay {
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(68, 74, 73, 0.55);
}

.cookie-card {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 760px;
  padding: 28px;
  background: $ipWhite;
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.cookie-illustration {
  width: 45%;
  max-width: 260px;
  height: auto;
  flex-shrink: 0;
}

.cookie-content {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.cookie-title {
  margin: 0 0 14px;
  font-size: 26px;
  font-weight: 700;
  color: $ipBlack;
}

.cookie-text {
  margin: 0 0 28px;
  font-size: 14px;
  line-height: 1.7;
  color: $ipGreyDark;

  a {
    color: $ipGreyDark;
    text-decoration: underline;

    &:hover {
      color: $ipGreen;
    }
  }
}

.cookie-buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.cookie-btn {
  padding: 12px 22px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  border-radius: $btn-border-radius;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.95);
  }
}

.cookie-btn-primary {
  background: $ipGreen;
  border: 2px solid $ipGreen;
  color: $ipWhite;
}

.cookie-btn-outline {
  background: $ipWhite;
  border: 2px solid $ipGreen;
  color: $ipGreen;
}

.cookie-link-btn {
  padding: 12px 4px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  background: none;
  border: none;
  color: $ipGreyDark;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $ipBlack;
  }
}

.cookie-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid $greyLight;

  &:last-of-type {
    margin-bottom: 20px;
  }
}

.cookie-setting-text {
  display: flex;
  flex-direction: column;
}

.cookie-setting-name {
  font-size: 14px;
  font-weight: 600;
  color: $ipBlack;
}

.cookie-setting-desc {
  font-size: 12.5px;
  line-height: 1.5;
  color: $ipGreyDark;
}

.cookie-setting-fixed {
  font-size: 13px;
  font-weight: 600;
  color: $ipGreen;
  white-space: nowrap;
}

.cookie-toggle {
  position: relative;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
  border: none;
  border-radius: 13px;
  background: $ipGreyLighter;
  cursor: pointer;
  transition: background 0.2s ease;
  padding: 0;
}

.cookie-toggle-on {
  background: $ipGreen;
}

.cookie-toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: $ipWhite;
  transition: transform 0.2s ease;
}

.cookie-toggle-on .cookie-toggle-knob {
  transform: translateX(20px);
}

@media (max-width: 560px) {
  .cookie-card {
    flex-direction: column;
    padding: 20px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
  }

  .cookie-illustration {
    width: 40%;
    max-width: 150px;
  }

  .cookie-content {
    text-align: center;
  }

  .cookie-buttons {
    justify-content: center;
  }

  .cookie-setting {
    text-align: left;
  }
}

.cookie-fade-enter-active,
.cookie-fade-leave-active {
  transition: opacity 0.25s ease;

  .cookie-card {
    transition: transform 0.25s ease;
  }
}

.cookie-fade-enter,
.cookie-fade-leave-to {
  opacity: 0;

  .cookie-card {
    transform: scale(0.95);
  }
}
</style>
